import React, { ChangeEvent, ReactElement, useMemo } from 'react';
import { Box, Grid2, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import { GoodsElementFormContainerBox } from '../../../common/ResponsiveStyle';
import DeleteIcon from '@mui/icons-material/Delete';
import { DimensionTypeDimensionSR, DimensionTypeSR } from '../../../common/Types';
import {
    removeGoodsBatchEntityForPurchaseOrder,
    updateBatchPriceForPurchaseOrder,
    updateGoodsArticleForPurchaseOrder,
    updateGoodsQuantityForPurchaseOrder,
} from '../../../redux/actions/purchaseOrderActions';
import { GoodBatchEntity } from '../../../redux/reducers/goodsToSiteFormReducer';
import ArticleAutocomplete from '../../../common/components/autocomplete/ArticleAutocomplete';

type PurchaseOrderFormGoodsElementProps = {
    purchaseOrderedGoodsBatchEntity: GoodBatchEntity;
    addNewGoods(): void;
    canChangeValuableInfo: boolean;
};

// get all goods from site
// reduce() quantity of goods
// give those articles as choices with total quantity
export default function PurchaseOrderFormGoodsElement(props: PurchaseOrderFormGoodsElementProps): ReactElement {
    const { purchaseOrderedGoodsBatchEntity, addNewGoods, canChangeValuableInfo } = props;

    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const purchaseOrderedGoods = useAppSelector((state: RootState) => state.goodsToPurchaseOrderForm.goodBatchEntities);

    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);

    const dispatch = useAppDispatch();

    const handleRemovePurchaseOrderGood = () => {
        if (!!purchaseOrderedGoodsBatchEntity.index) {
            dispatch(removeGoodsBatchEntityForPurchaseOrder(purchaseOrderedGoodsBatchEntity.index));
        }
    };

    const handlePurchaseOrderGoodsOrderedQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(
                updateGoodsQuantityForPurchaseOrder({
                    index: purchaseOrderedGoodsBatchEntity.index,
                    quantity: +value,
                }),
            );
        }
    };

    const handlePurchaseOrderGoodsPurchasePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(
                updateBatchPriceForPurchaseOrder({
                    index: purchaseOrderedGoodsBatchEntity.index,
                    batchPrice: +value,
                }),
            );
        }
    };

    const handleSelectArticle = (articleId?: number) => {
        const article = articles?.find((a) => a.id === articleId);

        dispatch(
            updateGoodsQuantityForPurchaseOrder({
                index: purchaseOrderedGoodsBatchEntity.index,
                quantity: 0,
            }),
        );
        dispatch(updateGoodsArticleForPurchaseOrder({ index: purchaseOrderedGoodsBatchEntity.index, article }));
    };

    const handleEnterPressed = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const code = e.code;

        if (code === 'Enter' || code === 'NumpadEnter') {
            if (
                purchaseOrderedGoodsBatchEntity.index ===
                Math.max(...(purchaseOrderedGoods?.map((og) => og.index) ?? []))
            ) {
                addNewGoods();
            }
        }
    };

    const filteredArticles = useMemo(() => {
        if (!articles) {
            return [];
        }
        return articles.filter((a) => !purchaseOrderedGoods?.find((og) => og.goods.article?.id === a.id));
    }, [articles, purchaseOrderedGoods]);

    return (
        !!purchaseOrderedGoodsBatchEntity && (
            <GoodsElementFormContainerBox>
                <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                    <Grid2
                        size={{
                            xs: 12,
                            md: canChangeValuableInfo ? 6 : 9,
                            xl: canChangeValuableInfo ? 8 : 10,
                        }}
                    >
                        <Tooltip
                            title={
                                !!purchaseOrderedGoodsBatchEntity.goods.article
                                    ? purchaseOrderedGoodsBatchEntity.goods.article.title
                                    : ''
                            }
                        >
                            <div>
                                <ArticleAutocomplete
                                    articles={filteredArticles ?? []}
                                    isRequired
                                    selectArticle={handleSelectArticle}
                                    selectedArticleId={purchaseOrderedGoodsBatchEntity.goods.article?.id}
                                />
                            </div>
                        </Tooltip>
                    </Grid2>
                    <Grid2
                        size={{
                            xs: 12,
                            md: 3,
                            xl: 2,
                        }}
                    >
                        <TextField
                            error={
                                !!purchaseOrderedGoodsBatchEntity.goods.quantity &&
                                purchaseOrderedGoodsBatchEntity.goods.quantity > 0
                                    ? false
                                    : true
                            }
                            required
                            fullWidth
                            name="orderedQuantity"
                            label={
                                purchaseOrderedGoodsBatchEntity.goods.article?.dimensionType
                                    ? DimensionTypeSR[purchaseOrderedGoodsBatchEntity.goods.article.dimensionType]
                                    : 'Količina'
                            }
                            slotProps={{
                                htmlInput: { inputMode: 'decimal' },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {purchaseOrderedGoodsBatchEntity.goods.article?.dimensionType
                                                ? DimensionTypeDimensionSR[
                                                      purchaseOrderedGoodsBatchEntity.goods.article.dimensionType
                                                  ]
                                                : 'kom.'}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            type="number"
                            value={
                                !!purchaseOrderedGoodsBatchEntity.goods.quantity
                                    ? purchaseOrderedGoodsBatchEntity.goods.quantity
                                    : ''
                            }
                            variant="outlined"
                            onChange={handlePurchaseOrderGoodsOrderedQuantityChange}
                            onKeyDown={handleEnterPressed}
                        />
                    </Grid2>
                    {canChangeValuableInfo && (
                        <Grid2
                            size={{
                                xs: 12,
                                md: 3,
                                xl: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                name="purchasePricePerArticle"
                                label="Cena"
                                slotProps={{
                                    input: {
                                        endAdornment: companyDetails?.currency ? (
                                            <InputAdornment position="end">{companyDetails.currency}</InputAdornment>
                                        ) : (
                                            <></>
                                        ),
                                    },
                                }}
                                type="number"
                                value={
                                    !!purchaseOrderedGoodsBatchEntity.batch.purchasePricePerArticle
                                        ? purchaseOrderedGoodsBatchEntity.batch.purchasePricePerArticle
                                        : ''
                                }
                                variant="outlined"
                                onChange={handlePurchaseOrderGoodsPurchasePriceChange}
                                onKeyDown={handleEnterPressed}
                            />
                        </Grid2>
                    )}
                </Grid2>
                <Box style={{ flexGrow: 1 }} />
                <Tooltip title="Ukloni artikl" placement="left">
                    <IconButton sx={{ height: 40 }} onClick={handleRemovePurchaseOrderGood}>
                        <DeleteIcon color="error" />
                    </IconButton>
                </Tooltip>
            </GoodsElementFormContainerBox>
        )
    );
}
